import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import useTheme4A from '../components/useTheme4A';

const AXIOM_LOGO = 'https://customer-assets.emergentagent.com/job_sponsor-bot-clinical/artifacts/kgiafx8p_AxiomLogo.png';

function SetPinPage() {
  useTheme4A();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const handleSetPin = async () => {
    if (!pin || !confirmPin) {
      toast.error('Please enter PIN twice');
      return;
    }

    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }

    if (pin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }

    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/set-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin })
      });

      if (response.ok) {
        toast.success('PIN created successfully');
        navigate('/onboarding');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to set PIN');
      }
    } catch (error) {
      console.error('Set PIN error:', error);
      toast.error('An error occurred');
    }
  };

  const handleSkip = () => {
    navigate('/onboarding');
  };

  return (
    <div className="min-h-screen bg-blue-600 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 text-center">
          <img src={AXIOM_LOGO} alt="Axiom" className="h-20 mx-auto" />
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold tracking-tight text-white text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Create Your PIN
        </h1>
        <p className="text-base font-normal text-blue-100 leading-relaxed text-center mb-8" style={{ fontFamily: 'Inter, sans-serif' }}>
          Set up a 4-digit PIN for quick access to your account
        </p>

        {/* Form */}
        <div className="bg-white rounded-md p-6 space-y-4">
          <div>
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">4-Digit PIN</Label>
            <Input
              type="password"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 text-center text-2xl tracking-widest"
              data-testid="pin-input"
            />
          </div>

          <div>
            <Label className="text-xs font-bold text-zinc-900 uppercase mb-2 block">Confirm PIN</Label>
            <Input
              type="password"
              maxLength={4}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••"
              className="bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md h-12 text-center text-2xl tracking-widest"
              data-testid="confirm-pin-input"
            />
          </div>

          <Button
            onClick={handleSetPin}
            className="w-full bg-zinc-950 text-white hover:bg-zinc-800 rounded-md h-12 font-medium text-sm uppercase tracking-wide"
            data-testid="set-pin-btn"
          >
            Set PIN
          </Button>

          <button
            onClick={handleSkip}
            className="w-full text-sm text-zinc-600 hover:underline text-center"
            data-testid="skip-pin-btn"
          >
            Skip for now (you can set it later in settings)
          </button>
        </div>
      </div>
    </div>
  );
}

export default SetPinPage;